import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
  Table,
  Input,
  Tag,
  Button,
  Modal,
  Space,
  Card,
  Descriptions,
  message,
  Row,
  Col,
  Select,
  Tooltip,
  Spin,
  Typography,
  Tabs,
  Badge,
  DatePicker,
} from 'antd';
import {
  SearchOutlined,
  PlusOutlined,
  FilterOutlined,
  EyeOutlined,
  FilePdfOutlined,
  DownloadOutlined,
  FileSearchOutlined,
  SortAscendingOutlined,
  SortDescendingOutlined,
} from '@ant-design/icons';
import {
  supabase
} from '../lib/supabase';
import { sendWebhook, WEBHOOKS } from '../lib/webhooks';
import { generateOrderConfirmationPDF, savePDF, generateAndSaveSupplierPurchaseOrders } from '../lib/pdfGenerator';
import { Order, Product, OrderProduct, Leverancier } from '../types';
import { useOrderProducts } from '../hooks/useOrderProducts';
import NewOrderDialog from '../components/NewOrderDialog';
import dayjs from 'dayjs';
import { jsPDF } from 'jspdf';
import { formatPrice } from '../utils/format';
import { useProductPriceTiers } from '../hooks/useProductPriceTiers';
import { useAuth } from '../contexts/AuthContext';
import { calculateExpectedDeliveryDate } from '../utils/format';
import type { ColumnsType } from 'antd/es/table';

const { Title } = Typography;

const statusLabels: Record<string, string> = {
  nieuw: 'Nieuw',
  in_behandeling: 'In behandeling',
  verzonden: 'Verzonden',
  afgeleverd: 'Afgeleverd',
  geannuleerd: 'Geannuleerd'
};

const statusColors: Record<string, string> = {
  nieuw: 'blue',
  in_behandeling: 'orange',
  verzonden: 'purple',
  afgeleverd: 'green',
  geannuleerd: 'red'
};

export default function Orders() {
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string[]>([]);
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [newOrderDialogOpen, setNewOrderDialogOpen] = useState(false);
  const { orderProducts: selectedOrderProducts, loading: productsLoading } = useOrderProducts(selectedOrder?.id || '');
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [sortField, setSortField] = useState<string | null>('aangemaakt_op');
  const [sortOrder, setSortOrder] = useState<'ascend' | 'descend' | null>('descend');

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    try {
      console.log('Fetching orders...');
      
      let query = supabase
        .from('orders')
        .select(`
          id,
          ordernummer,
          klant_id,
          status,
          totaal_bedrag,
          bezorgadres,
          bezorg_postcode,
          bezorg_stad,
          bezorg_land,
          notities,
          verwachte_leverdatum,
          aangemaakt_op,
          bijgewerkt_op,
          eigen_referentie,
          gewenste_leverdatum
        `);
      
      // Now try adding the sort
      try {
        query = query.order('aangemaakt_op', { ascending: false });
        console.log('Sorting applied');
      } catch (sortError) {
        console.error('Error applying sort:', sortError);
        // Continue without sorting if it fails
      }
      
      if (statusFilter.length > 0) {
        query = query.in('status', statusFilter);
      }

      console.log('Executing query...');
      const { data, error } = await query;

      if (error) {
        console.error('Query error details:', error);
        throw error;
      }
      
      console.log('Orders fetched successfully:', data?.length || 0);
      setOrders(data || []);
    } catch (error) {
      console.error('Error fetching orders:', error);
      message.error('Er is een fout opgetreden bij het ophalen van de orders');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    fetchOrders();
    
    // Set up real-time subscription for orders
    const ordersSubscription = supabase
      .channel('orders-changes')
      .on('postgres_changes', { 
        event: '*', 
        schema: 'public', 
        table: 'orders' 
      }, () => {
        console.log('Orders table changed, refreshing data...');
        fetchOrders();
      })
      .subscribe();
    
    // Clean up subscription on unmount
    return () => {
      supabase.removeChannel(ordersSubscription);
    };
  }, [statusFilter, fetchOrders]); // Refetch when status filter changes

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const filteredOrders = orders.filter((order) =>
    (order.ordernummer || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
    order.id.toLowerCase().includes(searchQuery.toLowerCase()) ||
    order.bezorgadres?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    order.bezorg_stad?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const columns = useMemo<ColumnsType<Order>>(() => [
    {
      title: 'Order Nr.',
      key: 'ordernummer',
      sorter: true,
      sortOrder: sortField === 'ordernummer' ? sortOrder : null,
      render: (_: any, record: Order) => {
        const displayValue = record.ordernummer || record.id.slice(0, 8);
        return (
          <Typography.Text copyable>{displayValue}</Typography.Text>
        );
      },
    },
    {
      title: 'Eigen referentie',
      dataIndex: 'eigen_referentie',
      key: 'eigen_referentie',
      sorter: true,
      sortOrder: sortField === 'eigen_referentie' ? sortOrder : null,
      render: (eigen_referentie: string | undefined) => eigen_referentie || '-',
    },
    {
      title: 'Datum',
      dataIndex: 'aangemaakt_op',
      key: 'aangemaakt_op',
      sorter: true,
      render: (date: string) => dayjs(date).format('DD-MM-YYYY'),
    },
    {
      title: 'Gewenste leverdatum',
      dataIndex: 'gewenste_leverdatum',
      key: 'gewenste_leverdatum',
      sorter: true,
      sortOrder: sortField === 'gewenste_leverdatum' ? sortOrder : null,
      render: (date: string) => date ? new Date(date).toLocaleDateString('nl-NL') : '-',
      responsive: ['md' as any],
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      sorter: true,
      render: (status: string) => (
        <Tag color={statusColors[status]}>
          {statusLabels[status]}
        </Tag>
      ),
    },
    {
      title: 'Totaalbedrag',
      dataIndex: 'totaal_bedrag',
      key: 'totaal_bedrag',
      render: (amount: number) => formatPrice(amount),
      responsive: ['sm' as any],
    },
    {
      title: 'Acties',
      key: 'actions',
      render: (_: Record<string, unknown>, record: Order) => (
        <Space size="small">
          <Button
            type="primary"
            icon={<EyeOutlined />}
            onClick={() => setSelectedOrder(record)}
            size={isMobile ? 'small' : 'middle'}
          />
        </Space>
      ),
    },
  ], [isMobile, sortField, sortOrder]);

  const handleTableChange = (
    pagination: any,
    filters: any,
    sorter: any
  ) => {
    setSortField(sorter.field);
    setSortOrder(sorter.order);
  };

  const sortedOrders = useMemo(() => {
    if (!sortField || !sortOrder) {
      // Default sort by creation date descending if no sort specified
      return [...filteredOrders].sort((a, b) => 
        new Date(b.aangemaakt_op).getTime() - new Date(a.aangemaakt_op).getTime()
      );
    }

    return [...filteredOrders].sort((a, b) => {
      if (sortField === 'totaal_bedrag') {
        const aValue = a.totaal_bedrag || 0;
        const bValue = b.totaal_bedrag || 0;
        return sortOrder === 'ascend' 
          ? aValue - bValue
          : bValue - aValue;
      }

      if (sortField === 'aangemaakt_op') {
        const aValue = a.aangemaakt_op || '';
        const bValue = b.aangemaakt_op || '';
        return sortOrder === 'ascend'
          ? new Date(aValue).getTime() - new Date(bValue).getTime()
          : new Date(bValue).getTime() - new Date(aValue).getTime();
      }

      if (sortField === 'gewenste_leverdatum') {
        const aValue = a.gewenste_leverdatum || '';
        const bValue = b.gewenste_leverdatum || '';
        return sortOrder === 'ascend'
          ? new Date(aValue || '2099-12-31').getTime() - new Date(bValue || '2099-12-31').getTime()
          : new Date(bValue || '2099-12-31').getTime() - new Date(aValue || '2099-12-31').getTime();
      }

      if (sortField === 'ordernummer') {
        const aValue = a.ordernummer || '';
        const bValue = b.ordernummer || '';
        return sortOrder === 'ascend'
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }

      if (sortField === 'status') {
        const aValue = a.status || '';
        const bValue = b.status || '';
        return sortOrder === 'ascend'
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }

      return 0;
    });
  }, [filteredOrders, sortField, sortOrder]);

  return (
    <div style={{ padding: '24px' }}>
      <Card
        style={{
          borderRadius: '8px',
        }}
      >
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <div style={{ 
            display: 'flex', 
            flexDirection: isMobile ? 'column' : 'row',
            justifyContent: 'space-between', 
            alignItems: isMobile ? 'flex-start' : 'center',
            gap: isMobile ? '16px' : '0'
          }}>
            <Space 
              size="middle" 
              wrap={isMobile}
              style={{ 
                width: '100%',
                display: 'flex',
                flexWrap: 'wrap',
                justifyContent: isMobile ? 'flex-start' : 'flex-end'
              }}
            >
              <Input
                placeholder="Zoek op order ID"
                prefix={<SearchOutlined />}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{ 
                  width: isMobile ? '100%' : 200 
                }}
              />
              <Select
                mode="multiple"
                placeholder="Filter op status"
                value={statusFilter}
                onChange={setStatusFilter}
                style={{ 
                  width: isMobile ? '100%' : 300 
                }}
                maxTagCount={isMobile ? 1 : "responsive"}
                allowClear
                dropdownMatchSelectWidth={false}
                options={Object.entries(statusLabels).map(([value, label]) => ({
                  value,
                  label: isMobile ? label : (
                    <Space>
                      <Tag color={statusColors[value]}>{label}</Tag>
                    </Space>
                  ),
                }))}
              />
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => setNewOrderDialogOpen(true)}
                style={{ 
                  width: isMobile ? '100%' : 'auto' 
                }}
              >
                Nieuwe order
              </Button>
            </Space>
          </div>

          <Table
            dataSource={sortedOrders}
            columns={columns}
            rowKey="id"
            loading={loading}
            pagination={{
              pageSize: 10,
              showSizeChanger: window.innerWidth >= 768,
              showTotal: (total, range) => `${range[0]}-${range[1]} van ${total} orders`,
            }}
            scroll={{ x: 'max-content' }}
            onChange={handleTableChange}
          />
        </Space>
      </Card>

      <Modal
        title="Order Details"
        open={!!selectedOrder}
        onCancel={() => setSelectedOrder(null)}
        footer={[
          <Button 
            key="pdf" 
            type="primary" 
            icon={<FilePdfOutlined />}
            onClick={async () => {
              if (!selectedOrder) return;
              
              try {
                // Show loading message
                const loadingMessage = message.loading('PDF aan het genereren...', 0);
                
                // Get fresh product details with levertijd_dagen
                const { data: freshProductDetails, error: productError } = await supabase
                  .from('order_products')
                  .select(`
                    id,
                    order_id,
                    product_id,
                    hoeveelheid,
                    prijs_per_stuk,
                    product:producten (
                      id,
                      naam,
                      beschrijving,
                      artikelnummer,
                      prijs,
                      aangemaakt_op,
                      bijgewerkt_op,
                      levertijd_dagen
                    )
                  `)
                  .eq('order_id', selectedOrder.id);
                
                if (productError) {
                  console.error('Error fetching product details:', productError);
                  message.error('Error fetching product details');
                  loadingMessage();
                  return;
                }
                
                // Generate the PDF with the fresh data that includes levertijd_dagen
                const pdfBlob = await generateOrderConfirmationPDF(
                  selectedOrder,
                  freshProductDetails as unknown as OrderProduct[],
                  {
                    name: 'RE-PACKED',
                    address: 'Zure Vischsteeg 12',
                    postalCode: '3143 BB',
                    city: 'Maassluis',
                    country: 'Nederland',
                    contactEmail: 'info@re-packed.nl',
                    contactPhone: '+31612962202',
                  }
                );
                
                // Store PDF in Supabase Storage
                const orderNumber = selectedOrder.ordernummer || selectedOrder.id.slice(0, 8);
                const pdfFileName = `orderbevestiging-${orderNumber}.pdf`;
                
                try {
                  const { data: storageData, error: storageError } = await supabase
                    .storage
                    .from('order-pdfs') // Make sure this bucket exists
                    .upload(`orders/${selectedOrder.id}/${pdfFileName}`, pdfBlob, {
                      contentType: 'application/pdf',
                      upsert: true
                    });
                    
                  if (storageError) {
                    console.error('Error storing PDF in Supabase:', storageError);
                  } else {
                    console.log('PDF stored in Supabase Storage:', storageData);
                    
                    // Add PDF URL reference to the order
                    const { error: updateError } = await supabase
                      .from('orders')
                      .update({ 
                        pdf_url: `orders/${selectedOrder.id}/${pdfFileName}` 
                      })
                      .eq('id', selectedOrder.id);
                      
                    if (updateError) {
                      console.error('Error updating order with PDF URL:', updateError);
                    }
                    
                    // Also send webhook with download notification
                    try {
                      // Convert to base64 for webhook
                      const pdfBase64 = await new Promise<string>((resolve) => {
                        const reader = new FileReader();
                        reader.onloadend = () => {
                          const base64data = reader.result as string;
                          // Remove the data URL prefix
                          const base64Content = base64data.split(',')[1];
                          resolve(base64Content);
                        };
                        reader.readAsDataURL(pdfBlob);
                      });
                      
                      // Get logo as base64 too
                      let logoBase64 = null;
                      try {
                        const logoUrl = 'https://ivtllicbozkouaeurjjc.supabase.co/storage/v1/object/public/logos/repacked-logo.png';
                        const logoResponse = await fetch(logoUrl);
                        const logoBlob = await logoResponse.blob();
                        
                        logoBase64 = await new Promise<string>((resolve) => {
                          const reader = new FileReader();
                          reader.onloadend = () => {
                            const base64data = reader.result as string;
                            // Remove the data URL prefix
                            const base64Content = base64data.split(',')[1];
                            resolve(base64Content);
                          };
                          reader.readAsDataURL(logoBlob);
                        });
                      } catch (logoError) {
                        console.error('Error converting logo to base64:', logoError);
                      }
                      
                      await sendWebhook(WEBHOOKS.ORDER_PDF_DOWNLOAD, {
                        event: 'order.pdf_downloaded',
                        is_download_only: true,
                        skip_email_notification: true,
                        pdf_purpose: 'manual_download',
                        order_id: selectedOrder.id,
                        ordernummer: selectedOrder.ordernummer,
                        klant_id: selectedOrder.klant_id,
                        status: selectedOrder.status,
                        pdf_base64: pdfBase64,
                        pdf_filename: pdfFileName,
                        logo_url: 'https://ivtllicbozkouaeurjjc.supabase.co/storage/v1/object/public/logos/repacked-logo.png',
                        logo_base64: logoBase64
                      });
                    } catch (webhookError) {
                      console.error('Error sending webhook:', webhookError);
                    }
                  }
                } catch (storageError) {
                  console.error('Error with storage operation:', storageError);
                }
                
                // Close loading message
                loadingMessage();
                
                // Save the PDF
                savePDF(pdfBlob, pdfFileName);
                
                message.success('Orderbevestiging PDF is gedownload');
              } catch (error) {
                console.error('Error generating PDF:', error);
                message.error('Er is een fout opgetreden bij het genereren van de PDF');
              }
            }}
          >
            Download PDF
          </Button>,
          <Button key="close" onClick={() => setSelectedOrder(null)}>
            Sluiten
          </Button>
        ]}
        width={800}
      >
        {selectedOrder && (
          <Spin spinning={productsLoading}>
            <Descriptions bordered column={2}>
              <Descriptions.Item label="Order Nr." span={2}>
                <Typography.Text copyable>{selectedOrder.ordernummer || selectedOrder.id.slice(0, 8)}</Typography.Text>
              </Descriptions.Item>
              <Descriptions.Item label="Status">
                <Tag color={statusColors[selectedOrder.status]}>
                  {statusLabels[selectedOrder.status]}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Totaal bedrag">
                {new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR' })
                  .format(selectedOrder.totaal_bedrag)}
              </Descriptions.Item>
              <Descriptions.Item label="Eigen referentie" span={2}>
                {selectedOrder.eigen_referentie || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Gewenste leverdatum" span={2}>
                {selectedOrder.gewenste_leverdatum ? 
                  new Date(selectedOrder.gewenste_leverdatum).toLocaleDateString('nl-NL') : 
                  '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Bezorgadres" span={2}>
                {selectedOrder.bezorgadres}<br />
                {selectedOrder.bezorg_postcode} {selectedOrder.bezorg_stad}<br />
                {selectedOrder.bezorg_land}
              </Descriptions.Item>
              <Descriptions.Item label="Notities" span={2}>
                {selectedOrder.notities || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Aangemaakt op">
                {new Date(selectedOrder.aangemaakt_op).toLocaleString('nl-NL')}
              </Descriptions.Item>
              <Descriptions.Item label="Laatst bijgewerkt">
                {new Date(selectedOrder.bijgewerkt_op).toLocaleString('nl-NL')}
              </Descriptions.Item>
            </Descriptions>

            <h4 style={{ marginTop: 24 }}>Producten</h4>
            <Table
              dataSource={selectedOrderProducts}
              columns={[
                {
                  title: 'Product',
                  dataIndex: 'product',
                  key: 'product',
                  render: (product: Product) => product.naam,
                },
                {
                  title: 'Aantal',
                  dataIndex: 'hoeveelheid',
                  key: 'hoeveelheid',
                },
                {
                  title: 'Prijs per stuk',
                  dataIndex: 'prijs_per_stuk',
                  key: 'prijs_per_stuk',
                  render: (price: number) => formatPrice(price),
                },
                                  {
                    title: 'Totaal',
                    key: 'total',
                    render: (record: OrderProduct) => formatPrice(record.hoeveelheid * record.prijs_per_stuk),
                  },
                  {
                    title: 'Gewenste leverdatum',
                    key: 'delivery_date',
                    render: () => 
                      selectedOrder.gewenste_leverdatum ? 
                      new Date(selectedOrder.gewenste_leverdatum).toLocaleDateString('nl-NL') : 
                      '-',
                  }
              ]}
              pagination={false}
              rowKey="id"
            />
          </Spin>
        )}
      </Modal>

      {newOrderDialogOpen && (
        <NewOrderDialog
          open={newOrderDialogOpen}
          onClose={() => setNewOrderDialogOpen(false)}
          onSubmit={async (orderData) => {
            try {
              // Get current user and ensure they are authenticated
              const { data: { user }, error: userError } = await supabase.auth.getUser();
              
              if (userError) {
                console.error('Auth error:', userError);
                message.error('Authenticatiefout. Log opnieuw in.');
                
                // Attempt to refresh the session
                const { error: refreshError } = await supabase.auth.refreshSession();
                if (refreshError) {
                  console.error('Session refresh error:', refreshError);
                  // Redirect to login if session refresh fails
                  window.location.href = '/login';
                }
                return;
              }
              
              if (!user) {
                message.error('U bent niet ingelogd. Log in om een order te plaatsen.');
                window.location.href = '/login';
                return;
              }

              // First get product prices and tiered pricing data
              const { data: products } = await supabase
                .from('producten')
                .select('id, prijs')
                .in('id', orderData.products.map(p => p.product_id));

              if (!products) throw new Error('Could not fetch product prices');
              
              // Get tiered pricing data for all the products in the order
              const { data: priceTiers, error: tierError } = await supabase
                .from('product_price_tiers')
                .select('*')
                .in('product_id', orderData.products.map(p => p.product_id))
                .order('min_quantity', { ascending: true });
                
              if (tierError) {
                console.error('Error fetching tiered pricing:', tierError);
                // Continue with base pricing if tier data can't be fetched
              }

              // Calculate total amount with tiered pricing
              const totalAmount = orderData.products.reduce((sum, orderProduct) => {
                const product = products.find(p => p.id === orderProduct.product_id);
                if (!product) return sum;
                
                // Apply tiered pricing if available
                let pricePerItem = product.prijs; // Default to base price
                
                if (priceTiers && priceTiers.length > 0) {
                  // Get applicable tiers for this product and quantity
                  const applicableTiers = priceTiers
                    .filter(tier => 
                      tier.product_id === orderProduct.product_id && 
                      tier.min_quantity <= orderProduct.hoeveelheid
                    )
                    .sort((a, b) => b.min_quantity - a.min_quantity);
                  
                  // Use the highest applicable tier price, or base price if no tiers apply
                  if (applicableTiers.length > 0) {
                    pricePerItem = applicableTiers[0].price;
                  }
                }
                
                return sum + (pricePerItem * orderProduct.hoeveelheid);
              }, 0);

              // Get more product details to calculate expected delivery date
              const { data: fullProducts } = await supabase
                .from('producten')
                .select('id, levertijd_dagen')
                .in('id', orderData.products.map(p => p.product_id));
              
              // Calculate the expected delivery date based on the product with the longest delivery time
              let longestDeliveryDays = 0;
              if (fullProducts) {
                fullProducts.forEach(product => {
                  if (product.levertijd_dagen && product.levertijd_dagen > longestDeliveryDays) {
                    longestDeliveryDays = product.levertijd_dagen;
                  }
                });
              }
              
              // Calculate the expected delivery date
              let verwachteBezorgdatum = null;
              if (longestDeliveryDays > 0) {
                const date = new Date();
                date.setDate(date.getDate() + longestDeliveryDays);
                verwachteBezorgdatum = date.toISOString();
              }

              // Generate a company-specific order number with guaranteed uniqueness
              const generateClientOrderNumber = () => {
                // Ensure we have a valid user ID
                if (!user || !user.id) {
                  throw new Error('User ID is required for order number generation');
                }
                
                const now = new Date();
                // Get last 2 digits of year
                const shortYear = now.getFullYear().toString().slice(-2);
                
                // Create a company code that's guaranteed to be unique
                // Take first 6 chars of user ID (without hyphens) to ensure uniqueness
                // UUID format is typically: xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx
                const cleanUserId = user.id.replace(/-/g, '');
                const companyCode = cleanUserId.slice(0, 6).toUpperCase();
                
                // Get current date components
                const month = (now.getMonth() + 1).toString().padStart(2, '0');
                const day = now.getDate().toString().padStart(2, '0');
                
                // Get time components with milliseconds for perfect uniqueness
                const hours = now.getHours().toString().padStart(2, '0');
                const minutes = now.getMinutes().toString().padStart(2, '0');
                const seconds = now.getSeconds().toString().padStart(2, '0');
                const ms = now.getMilliseconds().toString().padStart(3, '0');
                
                // Create a timestamp-based sequence number that resets daily
                // Format: HHMMSSMMM (hour, minute, second, millisecond)
                const timeSequence = parseInt(`${hours}${minutes}${seconds}${ms}`);
                
                // Convert to a 4-digit sequence starting from 0001, max 9999
                // We take modulo of the time sequence to get a number between 0-9998, then add 1
                const seqNumber = (timeSequence % 9999) + 1;
                
                // Format the sequential part to 4 digits with leading zeros
                const sequentialNumber = seqNumber.toString().padStart(4, '0');
                
                // Format: ORD25-COMPANY-0001 
                return `ORD${shortYear}-${companyCode}-${sequentialNumber}`;
              };

              // Create order with the correct total amount and a client-generated order number
              const { data: order, error: orderError } = await supabase
                .from('orders')
                .insert([{
                  klant_id: user.id,
                  bezorgadres: orderData.bezorgadres,
                  bezorg_postcode: orderData.bezorg_postcode,
                  bezorg_stad: orderData.bezorg_stad,
                  bezorg_land: orderData.bezorg_land || 'Nederland',
                  notities: orderData.notities,
                  eigen_referentie: orderData.eigen_referentie,
                  gewenste_leverdatum: orderData.gewenste_leverdatum,
                  status: 'nieuw',
                  totaal_bedrag: totalAmount,
                  ordernummer: generateClientOrderNumber() // Generate a unique order number client-side
                }])
                .select()
                .single();

              if (orderError) {
                console.error('Error creating order:', orderError);
                if (orderError.code === '401') {
                  message.error('U bent niet geautoriseerd om deze actie uit te voeren. Log opnieuw in.');
                  await supabase.auth.refreshSession();
                } else {
                  message.error(`Fout bij aanmaken order: ${orderError.message}`);
                }
                throw orderError;
              }

              if (!order) {
                throw new Error('No order data returned');
              }

              // Insert order products with their correct tiered prices
              const { error: productsError } = await supabase
                .from('order_products')
                .insert(
                  orderData.products.map((product) => {
                    const productInfo = products.find(p => p.id === product.product_id);
                    if (!productInfo) throw new Error(`Product ${product.product_id} not found`);
                    
                    // Apply tiered pricing if available
                    let pricePerItem = productInfo.prijs; // Default to base price
                    
                    if (priceTiers && priceTiers.length > 0) {
                      // Get applicable tiers for this product and quantity
                      const applicableTiers = priceTiers
                        .filter(tier => 
                          tier.product_id === product.product_id && 
                          tier.min_quantity <= product.hoeveelheid
                        )
                        .sort((a, b) => b.min_quantity - a.min_quantity);
                      
                      // Use the highest applicable tier price, or base price if no tiers apply
                      if (applicableTiers.length > 0) {
                        pricePerItem = applicableTiers[0].price;
                      }
                    }
                    
                    return {
                      order_id: order.id,
                      product_id: product.product_id,
                      hoeveelheid: product.hoeveelheid,
                      prijs_per_stuk: pricePerItem,
                    };
                  })
                );

              if (productsError) throw productsError;

              // Call webhook after order is successfully created
              try {
                // Fetch product details for the PDF
                const { data: productDetails } = await supabase
                  .from('order_products')
                  .select(`
                    id,
                    order_id,
                    product_id,
                    hoeveelheid,
                    prijs_per_stuk,
                    product:producten!product_id (
                      id,
                      naam,
                      beschrijving,
                      artikelnummer,
                      prijs,
                      aangemaakt_op,
                      bijgewerkt_op,
                      levertijd_dagen,
                      leverancier_id,
                      palletisering
                    )
                  `)
                  .eq('order_id', order.id);
                
                if (productDetails && productDetails.length > 0) {
                  console.log('Generating order confirmation PDF...');
                  
                  // Fetch customer company information first
                  let customerCompany: {
                    name: string;
                    contact_person?: string;
                    email?: string;
                    phone?: string;
                  } | undefined;
                  
                  try {
                    const { data: companyData, error: companyError } = await supabase
                      .from('companies')
                      .select('bedrijfsnaam, voornaam_financieel, achternaam_financieel, telefoon_financieel, email_financieel')
                      .eq('user_id', order.klant_id)
                      .single();
                    
                    if (companyData && !companyError) {
                      customerCompany = {
                        name: companyData.bedrijfsnaam,
                        contact_person: companyData.voornaam_financieel && companyData.achternaam_financieel 
                          ? `${companyData.voornaam_financieel} ${companyData.achternaam_financieel}` 
                          : undefined,
                        email: companyData.email_financieel,
                        phone: companyData.telefoon_financieel
                      };
                    }
                  } catch (error) {
                    console.error('Error fetching customer company data:', error);
                  }
                  
                  try {
                    // Define company info for the PDF
                    const companyInfo = {
                      name: 'RE-PACKED',
                      address: 'Zure Vischsteeg 12',
                      postalCode: '3143 BB',
                      city: 'Maassluis',
                      country: 'Nederland',
                      contactEmail: 'info@re-packed.nl',
                      contactPhone: '+31612962202',
                    };
                    
                    // Import and use the proper PDF generator function
                    const { generateOrderConfirmationPDF, generateAndSaveSupplierPurchaseOrders } = await import('../lib/pdfGenerator');
                    
                    // Generate a complete PDF with all order details and products
                    const pdfBlob = await generateOrderConfirmationPDF(
                      order, 
                      productDetails as unknown as OrderProduct[],
                      companyInfo
                    );
                    
                    // Store PDF in Supabase Storage
                    const pdfFileName = `orderbevestiging-${order.ordernummer || order.id.slice(0, 8)}.pdf`;
                    const { data: storageData, error: storageError } = await supabase
                      .storage
                      .from('order-pdfs') // Make sure this bucket exists
                      .upload(`orders/${pdfFileName}`, pdfBlob, {
                        contentType: 'application/pdf',
                        upsert: true
                      });
                      
                    if (storageError) {
                      console.error('Error storing PDF in Supabase:', storageError);
                    } else {
                      console.log('PDF stored in Supabase Storage:', storageData);
                      
                      // Add PDF URL reference to the order
                      const { error: updateError } = await supabase
                        .from('orders')
                        .update({ 
                          pdf_url: `orders/${pdfFileName}` 
                        })
                        .eq('id', order.id);
                        
                      if (updateError) {
                        console.error('Error updating order with PDF URL:', updateError);
                      }
                    }
                    
                    // Convert order confirmation PDF to base64
                    const pdfBase64 = await new Promise<string>((resolve) => {
                      const reader = new FileReader();
                      reader.onloadend = () => {
                        const base64data = reader.result as string;
                        // Remove the data URL prefix
                        const base64Content = base64data.split(',')[1];
                        resolve(base64Content);
                      };
                      reader.readAsDataURL(pdfBlob);
                    });
                    
                    // Also fetch the logo and convert to base64 for the webhook
                    let logoBase64 = null;
                    try {
                      const logoUrl = 'https://ivtllicbozkouaeurjjc.supabase.co/storage/v1/object/public/logos/repacked-logo.png';
                      const logoResponse = await fetch(logoUrl);
                      const logoBlob = await logoResponse.blob();
                      
                      logoBase64 = await new Promise<string>((resolve) => {
                        const reader = new FileReader();
                        reader.onloadend = () => {
                          const base64data = reader.result as string;
                          // Remove the data URL prefix
                          const base64Content = base64data.split(',')[1];
                          resolve(base64Content);
                        };
                        reader.readAsDataURL(logoBlob);
                      });
                      
                      console.log('Logo converted to base64 successfully');
                    } catch (logoError) {
                      console.error('Error converting logo to base64:', logoError);
                    }
                    
                    // Generate supplier purchase order PDFs
                    // 1. Get supplier information for all suppliers in this order
                    // Use type assertion to help TypeScript understand our data structure
                    type ProductWithSupplier = {
                      product: {
                        leverancier_id: string;
                        [key: string]: any;
                      } | null;
                    };
                    
                    const uniqueSupplierIds = Array.from(new Set(
                      (productDetails as unknown as ProductWithSupplier[])
                        .filter(product => product.product && product.product.leverancier_id)
                        .map(product => product.product!.leverancier_id)
                    ));
                    
                    console.log(`Found ${uniqueSupplierIds.length} unique suppliers in order`);
                    
                    if (uniqueSupplierIds.length > 0) {
                      // 2. Fetch supplier details
                      const { data: supplierData, error: supplierError } = await supabase
                        .from('leveranciers')
                        .select('id, naam, contactpersoon, email, telefoon')
                        .in('id', uniqueSupplierIds);
                        
                      if (supplierError) {
                        console.error('Error fetching supplier data:', supplierError);
                      } else if (supplierData && supplierData.length > 0) {
                        // Transform supplier data into the required format
                        const suppliersMap: {
                          [key: string]: {
                            id: string;
                            name: string;
                            contactPerson: string;
                            email?: string;
                            phone?: string;
                          }
                        } = {};
                        
                        supplierData.forEach(supplier => {
                          suppliersMap[supplier.id] = {
                            id: supplier.id,
                            name: supplier.naam,
                            contactPerson: supplier.contactpersoon,
                            email: supplier.email,
                            phone: supplier.telefoon
                          };
                        });
                        
                        // 3. Generate and save supplier purchase order PDFs
                        try {
                          const supplierPDFs = await generateAndSaveSupplierPurchaseOrders(
                            order,
                            productDetails as unknown as OrderProduct[],
                            suppliersMap,
                            customerCompany
                          );
                          
                          console.log(`Generated ${supplierPDFs.length} supplier purchase order PDFs`);
                          
                          // 4. Add supplier PDF URLs to the webhook data
                          if (supplierPDFs.length > 0) {
                            // We'll make an array of supplier PDF information to include in the webhook
                            const supplierPDFsInfo = await Promise.all(
                              supplierPDFs.map(async pdf => {
                                // Get the PDF file from storage to convert to base64
                                const fileName = pdf.storageUrl.split('/').pop() || '';
                                
                                try {
                                  // Get public URL and download the file
                                  const response = await fetch(pdf.storageUrl);
                                  const pdfBlob = await response.blob();
                                  
                                  // Convert PDF to base64
                                  const pdfBase64 = await new Promise<string>((resolve) => {
                                    const reader = new FileReader();
                                    reader.onloadend = () => {
                                      const base64data = reader.result as string;
                                      // Remove the data URL prefix
                                      const base64Content = base64data.split(',')[1];
                                      resolve(base64Content);
                                    };
                                    reader.readAsDataURL(pdfBlob);
                                  });
                                  
                                  return {
                                    supplier_id: pdf.supplierId,
                                    supplier_name: pdf.supplierName,
                                    pdf_url: pdf.storageUrl,
                                    pdf_filename: fileName,
                                    pdf_base64: pdfBase64
                                  };
                                } catch (error) {
                                  console.error(`Error processing supplier PDF ${fileName}:`, error);
                                  return {
                                    supplier_id: pdf.supplierId,
                                    supplier_name: pdf.supplierName,
                                    pdf_url: pdf.storageUrl,
                                    pdf_filename: fileName
                                  };
                                }
                              })
                            );
                            
                            console.log(`Processed ${supplierPDFsInfo.length} supplier PDFs for webhook`);
                            
                            // Store this for use in the webhook call
                            const supplierPdfDetails = {
                              supplier_pdfs: supplierPDFsInfo,
                              has_supplier_pdfs: true,
                              supplier_count: supplierPDFsInfo.length
                            };
                            
                            // Send to webhook with all order data
                            await sendWebhook(WEBHOOKS.ORDER_CREATED, {
                              event: 'order.created',
                              order_id: order.id,
                              ordernummer: order.ordernummer,
                              klant_id: order.klant_id,
                              status: order.status,
                              totaal_bedrag: order.totaal_bedrag,
                              eigen_referentie: order.eigen_referentie,
                              aangemaakt_op: order.aangemaakt_op,
                              producten: orderData.products.length,
                              pdf_base64: pdfBase64,
                              pdf_filename: `orderbevestiging-${order.ordernummer || order.id.slice(0, 8)}.pdf`,
                              logo_url: 'https://ivtllicbozkouaeurjjc.supabase.co/storage/v1/object/public/logos/repacked-logo.png',
                              logo_base64: logoBase64,
                              // Include customer company information
                              customer_company: customerCompany,
                              // Include supplier PDF information with base64 content
                              ...supplierPdfDetails
                            });
                            
                            console.log('Order confirmation with PDFs sent to webhook');
                            return; // We've already sent the webhook
                          }
                        } catch (supplierPdfError) {
                          console.error('Error generating supplier purchase order PDFs:', supplierPdfError);
                        }
                      }
                    }
                    
                    // Send to webhook with all order data
                    await sendWebhook(WEBHOOKS.ORDER_CREATED, {
                      event: 'order.created',
                      order_id: order.id,
                      ordernummer: order.ordernummer,
                      klant_id: order.klant_id,
                      status: order.status,
                      totaal_bedrag: order.totaal_bedrag,
                      eigen_referentie: order.eigen_referentie,
                      aangemaakt_op: order.aangemaakt_op,
                      producten: orderData.products.length,
                      pdf_base64: pdfBase64,
                      pdf_filename: `orderbevestiging-${order.ordernummer || order.id.slice(0, 8)}.pdf`,
                      logo_url: 'https://ivtllicbozkouaeurjjc.supabase.co/storage/v1/object/public/logos/repacked-logo.png',
                      logo_base64: logoBase64,
                      // Include customer company information
                      customer_company: customerCompany,
                      // Include supplier PDF information - we include URLs rather than base64 to avoid huge payloads
                      has_supplier_pdfs: uniqueSupplierIds.length > 0,
                      supplier_count: uniqueSupplierIds.length
                    });
                    
                    console.log('Order confirmation with PDFs sent to webhook');
                  } catch (pdfError) {
                    console.error('Error generating PDF:', pdfError);
                    
                    // If PDF generation fails, still send the webhook without the PDF
                    let logoBase64 = null;
                    try {
                      const logoUrl = 'https://ivtllicbozkouaeurjjc.supabase.co/storage/v1/object/public/logos/repacked-logo.png';
                      const logoResponse = await fetch(logoUrl);
                      const logoBlob = await logoResponse.blob();
                      
                      logoBase64 = await new Promise<string>((resolve) => {
                        const reader = new FileReader();
                        reader.onloadend = () => {
                          const base64data = reader.result as string;
                          // Remove the data URL prefix
                          const base64Content = base64data.split(',')[1];
                          resolve(base64Content);
                        };
                        reader.readAsDataURL(logoBlob);
                      });
                      
                      console.log('Logo converted to base64 successfully');
                    } catch (logoError) {
                      console.error('Error converting logo to base64:', logoError);
                    }
                    
                    await sendWebhook(WEBHOOKS.ORDER_CREATED, {
                      event: 'order.created',
                      order_id: order.id,
                      ordernummer: order.ordernummer,
                      klant_id: order.klant_id,
                      status: order.status,
                      totaal_bedrag: order.totaal_bedrag,
                      eigen_referentie: order.eigen_referentie,
                      aangemaakt_op: order.aangemaakt_op,
                      producten: orderData.products.length,
                      logo_url: 'https://ivtllicbozkouaeurjjc.supabase.co/storage/v1/object/public/logos/repacked-logo.png',
                      logo_base64: logoBase64,
                      // Include customer company information
                      customer_company: customerCompany
                    });
                  }
                } else {
                  // If no products details are available, send webhook without PDF
                  // Note: customerCompany is not available in this scope, but we can fetch it again
                  let customerCompany: {
                    name: string;
                    contact_person?: string;
                    email?: string;
                    phone?: string;
                  } | undefined;
                  
                  try {
                    const { data: companyData, error: companyError } = await supabase
                      .from('companies')
                      .select('bedrijfsnaam, voornaam_financieel, achternaam_financieel, telefoon_financieel, email_financieel')
                      .eq('user_id', order.klant_id)
                      .single();
                    
                    if (companyData && !companyError) {
                      customerCompany = {
                        name: companyData.bedrijfsnaam,
                        contact_person: companyData.voornaam_financieel && companyData.achternaam_financieel 
                          ? `${companyData.voornaam_financieel} ${companyData.achternaam_financieel}` 
                          : undefined,
                        email: companyData.email_financieel,
                        phone: companyData.telefoon_financieel
                      };
                    }
                  } catch (error) {
                    console.error('Error fetching customer company data:', error);
                  }
                  
                  let logoBase64 = null;
                  try {
                    const logoUrl = 'https://ivtllicbozkouaeurjjc.supabase.co/storage/v1/object/public/logos/repacked-logo.png';
                    const logoResponse = await fetch(logoUrl);
                    const logoBlob = await logoResponse.blob();
                    
                    logoBase64 = await new Promise<string>((resolve) => {
                      const reader = new FileReader();
                      reader.onloadend = () => {
                        const base64data = reader.result as string;
                        // Remove the data URL prefix
                        const base64Content = base64data.split(',')[1];
                        resolve(base64Content);
                      };
                      reader.readAsDataURL(logoBlob);
                    });
                    
                    console.log('Logo converted to base64 successfully');
                  } catch (logoError) {
                    console.error('Error converting logo to base64:', logoError);
                  }
                  
                  await sendWebhook(WEBHOOKS.ORDER_CREATED, {
                    event: 'order.created',
                    order_id: order.id,
                    ordernummer: order.ordernummer,
                    klant_id: order.klant_id,
                    status: order.status,
                    totaal_bedrag: order.totaal_bedrag,
                    eigen_referentie: order.eigen_referentie,
                    aangemaakt_op: order.aangemaakt_op,
                    producten: orderData.products.length,
                    logo_url: 'https://ivtllicbozkouaeurjjc.supabase.co/storage/v1/object/public/logos/repacked-logo.png',
                    logo_base64: logoBase64,
                    // Include customer company information
                    customer_company: customerCompany
                  });
                }
              } catch (webhookError) {
                console.error('Error sending webhook with PDF:', webhookError);
                // Don't throw the error - we don't want to fail the order creation if the webhook fails
              }

              message.success('Order succesvol aangemaakt');
              setNewOrderDialogOpen(false);
              
              // Make sure to refresh the orders list
              fetchOrders();
            } catch (error) {
              console.error('Error creating order:', error);
              message.error('Er is een fout opgetreden bij het aanmaken van de order');
            }
          }}
        />
      )}
    </div>
  );
}
